import type { TokenScope } from '@accounting/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useAuth } from '../auth/useAuth.js';
import { sessionClient } from './client.js';
import { uuid } from '../uuid.js';

/**
 * TanStack Query over the shared `ApiClient`.
 *
 * Two rules hold everywhere here:
 *
 * 1. **Every key starts with the company id.** Two companies' ledgers are
 *    fetched through identical URLs, and a cache keyed only by URL would serve
 *    one company's entries under the other's name after a switch. The switcher
 *    also resets the cache, so this is belt and braces — deliberately, because
 *    the failure it prevents is showing the wrong company's figures.
 *
 * 2. **Mutations never retry.** A retry of a POST that actually succeeded posts
 *    twice to an immutable ledger. Idempotency keys make a *deliberate* retry
 *    safe; an automatic one is not something the user asked for.
 */

export interface Paged<T> {
  readonly items: T[];
  readonly total?: number;
}

export interface Account {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';
  readonly vatCode: string | null;
  readonly standardChartCode: string | null;
  readonly isActive: boolean;
}

export interface JournalLine {
  readonly id: string;
  readonly accountId: string;
  readonly accountCode: string;
  readonly accountName: string;
  readonly debit: string;
  readonly credit: string;
  readonly vatCode: string | null;
  readonly vatAmount: string;
  readonly description: string | null;
}

export interface JournalEntry {
  readonly id: string;
  readonly date: string;
  readonly description: string;
  readonly reference: string | null;
  readonly entryType: string;
  readonly createdBy: string | null;
  readonly createdAt: string;
  readonly lines: JournalLine[];
  readonly totals: { readonly debit: string; readonly credit: string };
}

export interface JournalLineInput {
  readonly accountCode: string;
  readonly debit?: string;
  readonly credit?: string;
  readonly vatCode?: string;
  readonly vatAmount?: string;
  readonly description?: string;
  readonly dimensions?: Record<string, string>;
}

export interface CreateJournalEntry {
  readonly date: string;
  readonly description: string;
  readonly reference?: string;
  readonly entryType?: string;
  readonly lines: JournalLineInput[];
}

/** Query keys, in one place so an invalidation cannot miss a spelling. */
export const keys = {
  accounts: (company: string, query: string) => ['accounts', company, query] as const,
  journal: (company: string, query: string) => ['journal', company, query] as const,
  entry: (company: string, id: string) => ['journal', company, 'entry', id] as const,
  attachments: (company: string, query: string) =>
    ['attachments', company, query] as const,
  attachment: (company: string, id: string) =>
    ['attachments', company, 'one', id] as const,
  bankAccounts: (company: string, query: string) =>
    ['bank', company, 'accounts', query] as const,
  bankTransactions: (company: string, query: string) =>
    ['bank', company, 'transactions', query] as const,
  bankTransaction: (company: string, id: string) =>
    ['bank', company, 'transactions', 'one', id] as const,
  bankImports: (company: string, query: string) =>
    ['bank', company, 'imports', query] as const,
  bankReconciliation: (company: string) => ['bank', company, 'reconciliation'] as const,
  contacts: (company: string, query: string) => ['contacts', company, query] as const,
  invoices: (company: string, query: string) => ['invoices', company, query] as const,
  invoice: (company: string, id: string) => ['invoices', company, 'one', id] as const,
  dispatches: (company: string, query: string) =>
    ['invoices', company, 'dispatches', query] as const,
  vatPeriods: (company: string) => ['vat', company, 'periods'] as const,
  vatReport: (company: string, periodId: string) =>
    ['vat', company, 'periods', periodId, 'report'] as const,
  report: (company: string, name: string, query: string) =>
    ['reports', company, name, query] as const,
  dashboard: (company: string) => ['dashboard', company] as const,
  company: (company: string) => ['company', company] as const,
  inboundAddress: (company: string) => ['inbound-email', company, 'address'] as const,
  inboundSenders: (company: string) => ['inbound-email', company, 'senders'] as const,
  inboundMessages: (company: string, query: string) =>
    ['inbound-email', company, 'messages', query] as const,
  /*
    The two below break rule 1 -- no company id -- and must.

    They are user-scoped: the profile and the personal access tokens belong to
    the person, not to a set of books, and they do not change when the switcher
    moves. Keying them by company would cache one person's tokens under two
    keys and refetch both on every switch, implying a per-company token where
    ADR-007 deliberately has one per user.
  */
  audit: (company: string, query: string) => ['audit', company, query] as const,
  auditActions: (company: string) => ['audit', company, 'actions'] as const,
  trips: (company: string, query: string) => ['trips', company, query] as const,
  tripSummary: (company: string, year: number) =>
    ['trips', company, 'summary', year] as const,
  /*
    The two below are user-scoped, like profile and accessTokens: a push
    subscription is a browser on a device and belongs to the person, not to a
    set of books. Keying them by company would refetch on every switch and imply
    a per-company registration that does not exist.
  */
  pushConfig: () => ['push-config'] as const,
  pushDevices: () => ['push-devices'] as const,
  profile: () => ['profile'] as const,
  accessTokens: () => ['access-tokens'] as const,
  authorizationRequest: (id: string) => ['oauth-request', id] as const,
};

function queryString(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') {
      search.set(key, String(value));
    }
  }
  return search.size > 0 ? `?${search.toString()}` : '';
}

export interface AccountQuery {
  readonly type?: string | undefined;
  readonly search?: string | undefined;
  readonly includeInactive?: boolean | undefined;
}

export function useAccounts(query: AccountQuery = {}): UseQueryResult<Paged<Account>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    type: query.type,
    search: query.search,
    includeInactive: query.includeInactive === true ? 'true' : undefined,
    limit: 1000,
  });

  return useQuery({
    queryKey: keys.accounts(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<Account>>(`/v1/accounts${suffix}`);
    },
    enabled: api !== null,
  });
}

export interface JournalQuery {
  readonly reference?: string | undefined;
  readonly limit?: number | undefined;
  readonly offset?: number | undefined;
}

export function useJournalEntries(
  query: JournalQuery = {},
): UseQueryResult<Paged<JournalEntry>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    reference: query.reference,
    limit: query.limit ?? 50,
    offset: query.offset,
  });

  return useQuery({
    queryKey: keys.journal(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<JournalEntry>>(`/v1/journal-entries${suffix}`);
    },
    enabled: api !== null,
  });
}

export function useCreateJournalEntry(): UseMutationResult<
  JournalEntry,
  unknown,
  CreateJournalEntry
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateJournalEntry) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<JournalEntry>('/v1/journal-entries', {
        method: 'POST',
        body,
        // A fresh key per attempt. Reusing one across attempts would make a
        // corrected second submission return the first one's response.
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      // The entry list is stale, and so is every account balance it touched.
      void queryClient.invalidateQueries({ queryKey: ['journal', companyId ?? ''] });
      void queryClient.invalidateQueries({ queryKey: ['accounts', companyId ?? ''] });
    },
  });
}

export interface UpdateAccount {
  readonly name?: string;
  readonly vatCode?: string | null;
  readonly isActive?: boolean;
}

export function useUpdateAccount(): UseMutationResult<
  Account,
  unknown,
  { id: string; patch: UpdateAccount }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateAccount }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Account>(`/v1/accounts/${id}`, { method: 'PATCH', body: patch });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts', companyId ?? ''] });
    },
  });
}

export interface CreateAccount {
  readonly code: string;
  readonly name: string;
  readonly type: Account['type'];
  readonly vatCode?: string;
}

export function useCreateAccount(): UseMutationResult<Account, unknown, CreateAccount> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateAccount) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Account>('/v1/accounts', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts', companyId ?? ''] });
    },
  });
}

// -----------------------------------------------------------------------------
// Attachments
// -----------------------------------------------------------------------------

/**
 * The documents behind the postings.
 *
 * `storageKey` is deliberately absent — the API never returns it. It is an
 * internal address, and publishing one invites building a URL from it rather
 * than asking for one that is signed and expires.
 */
export interface Attachment {
  readonly id: string;
  readonly filename: string;
  readonly mimeType: string;
  readonly fileSizeBytes: number;
  readonly uploadChannel: string;
  /** `unlinked` until it documents an entry, then `linked` — and then permanent. */
  readonly state: 'unlinked' | 'draft_linked' | 'linked';
  readonly ocrText: string | null;
  readonly ocrConfidence: string | null;
  readonly createdAt: string;
}

export interface AttachmentLink {
  readonly entryId: string;
  readonly attachmentId: string;
  readonly entryDate: string;
  readonly entryDescription: string;
  readonly linkedAt: string;
}

export interface AttachmentDetail extends Attachment {
  readonly links: AttachmentLink[];
}

export interface UploadAttachment {
  readonly filename: string;
  readonly mimeType: string;
  readonly contentBase64: string;
}

export interface AttachmentQuery {
  readonly state?: Attachment['state'] | undefined;
  readonly limit?: number | undefined;
}

export function useAttachments(
  query: AttachmentQuery = {},
): UseQueryResult<Paged<Attachment>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ state: query.state, limit: query.limit ?? 200 });

  return useQuery({
    queryKey: keys.attachments(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<Attachment>>(`/v1/attachments${suffix}`);
    },
    enabled: api !== null,
  });
}

/** One attachment and what it documents. `null` disables the query. */
export function useAttachment(id: string | null): UseQueryResult<AttachmentDetail> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.attachment(companyId ?? '', id ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<AttachmentDetail>(`/v1/attachments/${id ?? ''}`);
    },
    enabled: api !== null && id !== null,
  });
}

export function useUploadAttachment(): UseMutationResult<
  Attachment,
  unknown,
  UploadAttachment
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UploadAttachment) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Attachment>('/v1/attachments', {
        method: 'POST',
        body: { ...body, channel: 'ui' },
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attachments', companyId ?? ''] });
    },
  });
}

/**
 * Attach a document to a journal entry.
 *
 * This is the call that starts the five-year retention. There is no unlink, and
 * from here the attachment cannot be deleted — the database enforces that, not
 * only the endpoint.
 */
export function useLinkAttachment(): UseMutationResult<
  AttachmentDetail,
  unknown,
  { id: string; entryId: string }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, entryId }: { id: string; entryId: string }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<AttachmentDetail>(`/v1/attachments/${id}/links`, {
        method: 'POST',
        body: { entryId },
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attachments', companyId ?? ''] });
    },
  });
}

/**
 * Delete an attachment nobody has used.
 *
 * Refused with 409 once it documents an entry. The UI only offers this for an
 * `unlinked` attachment, but the refusal is still surfaced rather than assumed
 * away: the state shown may be a moment old, and the database is the authority.
 */
export function useDeleteAttachment(): UseMutationResult<void, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<void>(`/v1/attachments/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['attachments', companyId ?? ''] });
    },
  });
}

export interface OpenedAttachment {
  /** A blob: URL for this tab. Revoke it when the tab is done with it. */
  readonly url: string;
  readonly filename: string;
  readonly mimeType: string;
}

/**
 * Fetch the document itself, and wrap it in a URL this tab can open.
 *
 * It used to ask the API for a pre-signed URL and point a tab at object
 * storage. That could not work in production, where the store is private and
 * the signed URL named `http://minio:9000` — an address that exists only inside
 * the server's container network. The button produced a link no browser could
 * resolve, and no test noticed, because tests sign against a MinIO they can
 * actually reach.
 *
 * Now the bytes come through the API under the session this client already
 * holds, and become a `blob:` URL belonging to this tab alone. Nothing
 * shareable is produced: a blob URL is scoped to the document that created it
 * and dies with it, unlike a signed URL that works for anyone holding the
 * string until it expires.
 *
 * A mutation rather than a query, and uncached — "fetch this once, now, because
 * somebody clicked" — and caching document bytes in the query cache would keep
 * one company's records in memory after switching to another.
 */
export function useOpenAttachment(): UseMutationResult<OpenedAttachment, unknown, string> {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      const { bytes, contentType, filename } = await api.requestBytes(
        `/v1/attachments/${id}/content`,
      );
      // Typed from the response so the viewer opens rather than downloads; the
      // API sends what the database recorded at upload.
      const blob = new Blob([bytes as BlobPart], { type: contentType });
      return {
        url: URL.createObjectURL(blob),
        filename: filename ?? 'bilag',
        mimeType: contentType,
      };
    },
  });
}

// -----------------------------------------------------------------------------
// Bank
// -----------------------------------------------------------------------------

export interface BankAccount {
  readonly id: string;
  readonly name: string;
  readonly iban: string | null;
  readonly currency: string;
  readonly accountId: string;
  readonly accountCode: string;
  readonly accountName: string;
  readonly isActive: boolean;
}

export interface BankTransaction {
  readonly id: string;
  readonly bankAccountId: string;
  readonly statementImportId: string;
  readonly date: string;
  /** Signed decimal string. Negative leaves the account. */
  readonly amount: string;
  readonly description: string | null;
  readonly reference: string | null;
  readonly balanceAfter: string | null;
  readonly statementRowIndex: number;
  readonly status: 'unmatched' | 'draft_matched' | 'matched';
  readonly importSource: string;
}

export interface BankReconciliation {
  readonly id: string;
  readonly bankTransactionId: string;
  readonly entryId: string;
  readonly entryDate: string;
  readonly entryDescription: string;
  readonly matchMethod: string;
  readonly confidenceScore: string | null;
  readonly matchedAt: string;
}

export interface BankTransactionDetail extends BankTransaction {
  readonly reconciliations: BankReconciliation[];
  readonly matchedAmount: string;
  /** Amount minus matchedAmount. Non-zero means the books do not account for it all. */
  readonly difference: string;
}

export interface StatementImport {
  readonly id: string;
  readonly bankAccountId: string;
  readonly source: string;
  readonly fileName: string;
  readonly fileSha256: string;
  readonly rowCount: number;
  readonly firstDate: string;
  readonly lastDate: string;
  readonly openingBalance: string | null;
  readonly closingBalance: string | null;
  readonly importedAt: string;
}

export interface StatementImportResult {
  readonly statementImportId: string;
  readonly reader: string;
  readonly alreadyImported: boolean;
  readonly rowsRead: number;
  readonly imported: number;
  readonly duplicates: number;
  readonly skipped: { readonly line: number; readonly reason: string }[];
  readonly firstDate: string;
  readonly lastDate: string;
  readonly openingBalance: string | null;
  readonly closingBalance: string | null;
  readonly continuity: {
    readonly checked: boolean;
    readonly previousClosingBalance: string | null;
    readonly openingBalance: string | null;
    readonly matches: boolean | null;
  };
}

export interface ReconciliationRow {
  readonly bankAccountId: string;
  readonly bankAccountName: string;
  readonly accountCode: string;
  readonly unmatchedCount: number;
  readonly unmatchedTotal: string;
  readonly statementClosingBalance: string | null;
}

export interface ReconcileResult {
  readonly bankTransactionId: string;
  readonly entryId: string;
  readonly entryCreated: boolean;
  readonly status: BankTransaction['status'];
  readonly matchedAmount: string;
  readonly difference: string;
}

/**
 * How a transaction is matched.
 *
 * Two shapes, and picking the wrong one has consequences: an incoming payment
 * against an invoice already booked has to settle the debtor, and booking it
 * fresh against a revenue account instead credits revenue twice for one sale
 * and pays VAT on it twice.
 */
export type ReconcileCommand =
  | { readonly entryId: string }
  | {
      readonly contraAccountCode: string;
      readonly description?: string;
      readonly date?: string;
    };

export function useBankAccounts(
  options: { readonly includeInactive?: boolean } = {},
): UseQueryResult<Paged<BankAccount>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    includeInactive: options.includeInactive === true ? 'true' : undefined,
  });

  return useQuery({
    queryKey: keys.bankAccounts(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<BankAccount>>(`/v1/bank/accounts${suffix}`);
    },
    enabled: api !== null,
  });
}

export interface CreateBankAccount {
  readonly name: string;
  readonly accountCode: string;
  /** Set when `accountCode` does not exist yet: it is created alongside. */
  readonly newAccountName?: string;
  readonly iban?: string;
  readonly currency?: string;
}

export function useCreateBankAccount(): UseMutationResult<
  BankAccount,
  unknown,
  CreateBankAccount
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateBankAccount) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<BankAccount>('/v1/bank/accounts', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank', companyId ?? ''] });
      // A bank account claims a ledger account, so the chart's view of it changed.
      void queryClient.invalidateQueries({ queryKey: ['accounts', companyId ?? ''] });
    },
  });
}

export interface BankTransactionQuery {
  readonly bankAccountId?: string | undefined;
  readonly status?: BankTransaction['status'] | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly limit?: number | undefined;
}

export function useBankTransactions(
  query: BankTransactionQuery = {},
): UseQueryResult<Paged<BankTransaction>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    bankAccountId: query.bankAccountId,
    status: query.status,
    from: query.from,
    to: query.to,
    limit: query.limit ?? 200,
  });

  return useQuery({
    queryKey: keys.bankTransactions(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<BankTransaction>>(`/v1/bank/transactions${suffix}`);
    },
    enabled: api !== null,
  });
}

/** One transaction, its matches and the remaining difference. `null` disables. */
export function useBankTransaction(
  id: string | null,
): UseQueryResult<BankTransactionDetail> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.bankTransaction(companyId ?? '', id ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<BankTransactionDetail>(`/v1/bank/transactions/${id ?? ''}`);
    },
    enabled: api !== null && id !== null,
  });
}

export function useStatementImports(
  query: { readonly bankAccountId?: string | undefined } = {},
): UseQueryResult<Paged<StatementImport>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ bankAccountId: query.bankAccountId, limit: 50 });

  return useQuery({
    queryKey: keys.bankImports(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<StatementImport>>(`/v1/bank/imports${suffix}`);
    },
    enabled: api !== null,
  });
}

/**
 * The difference between the bank and the books, per bank account.
 *
 * The Bookkeeping Act requires this to be *shown*, not merely to exist, which
 * is why it is at the top of the page rather than behind a report link.
 */
export function useReconciliationSummary(): UseQueryResult<Paged<ReconciliationRow>> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.bankReconciliation(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<ReconciliationRow>>('/v1/bank/reconciliation');
    },
    enabled: api !== null,
  });
}

export interface ImportStatementCommand {
  readonly bankAccountId: string;
  readonly fileName: string;
  readonly contentBase64: string;
  readonly reader?: string | undefined;
}

export function useImportStatement(): UseMutationResult<
  StatementImportResult,
  unknown,
  ImportStatementCommand
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ bankAccountId, fileName, contentBase64, reader }: ImportStatementCommand) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<StatementImportResult>(
        `/v1/bank/accounts/${bankAccountId}/import`,
        {
          method: 'POST',
          body: {
            fileName,
            contentBase64,
            ...(reader === undefined || reader === '' ? {} : { reader }),
          },
          idempotencyKey: uuid(),
        },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank', companyId ?? ''] });
    },
  });
}

export function useReconcile(): UseMutationResult<
  ReconcileResult,
  unknown,
  { id: string; command: ReconcileCommand }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, command }: { id: string; command: ReconcileCommand }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<ReconcileResult>(`/v1/bank/transactions/${id}/reconcile`, {
        method: 'POST',
        body: command,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank', companyId ?? ''] });
      // Booking fresh creates a journal entry, so the ledger changed too.
      void queryClient.invalidateQueries({ queryKey: ['journal', companyId ?? ''] });
    },
  });
}

/**
 * Remove a match.
 *
 * The entry id is a path segment, not a body field. The link goes; the journal
 * entry does not — entries are immutable, so one matched in error is corrected
 * by counter-entry.
 */
export function useUnreconcile(): UseMutationResult<
  ReconcileResult,
  unknown,
  { id: string; entryId: string }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, entryId }: { id: string; entryId: string }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<ReconcileResult>(
        `/v1/bank/transactions/${id}/reconcile/${entryId}`,
        { method: 'DELETE' },
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['bank', companyId ?? ''] });
    },
  });
}

// -----------------------------------------------------------------------------
// Contacts
// -----------------------------------------------------------------------------

export interface Contact {
  readonly id: string;
  readonly name: string;
  readonly cvr: string | null;
  readonly ean: string | null;
  readonly email: string | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly zip: string | null;
  readonly country: string;
  readonly type: 'customer' | 'supplier' | 'both';
  readonly isActive: boolean;
}

export interface CreateContact {
  readonly name: string;
  readonly type: Contact['type'];
  readonly cvr?: string;
  readonly ean?: string;
  readonly email?: string;
  readonly address?: string;
  readonly city?: string;
  readonly zip?: string;
  readonly country?: string;
}

/**
 * What may be changed on a contact.
 *
 * `undefined` leaves a field alone; `null` **clears** it. Those are different
 * intentions, and `''` is neither — an empty string fails the eight-digit CVR
 * check rather than removing the CVR, so the form has to send null.
 */
export interface UpdateContact {
  readonly name?: string | undefined;
  readonly type?: Contact['type'] | undefined;
  readonly cvr?: string | null | undefined;
  readonly ean?: string | null | undefined;
  readonly email?: string | null | undefined;
  readonly address?: string | null | undefined;
  readonly city?: string | null | undefined;
  readonly zip?: string | null | undefined;
  readonly country?: string | undefined;
  readonly isActive?: boolean | undefined;
}

export function useContacts(
  query: { readonly type?: Contact['type'] | undefined; readonly includeInactive?: boolean } = {},
): UseQueryResult<Paged<Contact>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    type: query.type,
    includeInactive: query.includeInactive === true ? 'true' : undefined,
    limit: 1000,
  });

  return useQuery({
    queryKey: keys.contacts(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<Contact>>(`/v1/invoices/contacts${suffix}`);
    },
    enabled: api !== null,
  });
}

export function useCreateContact(): UseMutationResult<Contact, unknown, CreateContact> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateContact) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Contact>('/v1/invoices/contacts', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', companyId ?? ''] });
    },
  });
}

export function useUpdateContact(): UseMutationResult<
  Contact,
  unknown,
  { id: string; patch: UpdateContact }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateContact }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Contact>(`/v1/invoices/contacts/${id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contacts', companyId ?? ''] });
      // An invoice row shows its contact's name.
      void queryClient.invalidateQueries({ queryKey: ['invoices', companyId ?? ''] });
    },
  });
}

// -----------------------------------------------------------------------------
// Invoices
// -----------------------------------------------------------------------------

export interface InvoiceLine {
  readonly id: string;
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly lineTotal: string;
  readonly vatCode: string | null;
  readonly vatAmount: string;
  readonly accountId: string | null;
  readonly accountCode: string | null;
  readonly sortOrder: number;
}

export interface Invoice {
  readonly id: string;
  /** Null while a draft. Allocated at issue, so a deleted draft leaves no gap. */
  readonly number: string | null;
  readonly numberYear: number | null;
  readonly numberSequence: number | null;
  readonly type: 'outgoing' | 'incoming';
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly date: string;
  readonly dueDate: string;
  readonly status: 'draft' | 'sent' | 'received' | 'paid' | 'overdue' | 'cancelled';
  readonly currency: string;
  readonly subtotal: string;
  readonly vatAmount: string;
  readonly total: string;
  readonly entryId: string | null;
  readonly createdAt: string;
  readonly lines: InvoiceLine[];
}

export interface InvoiceLineInput {
  readonly description: string;
  readonly quantity: string;
  readonly unitPrice: string;
  readonly accountCode?: string;
  readonly vatCode?: string;
  readonly vatAmount?: string;
}

export interface CreateInvoice {
  readonly contactId?: string;
  readonly date: string;
  readonly dueDate: string;
  readonly lines: InvoiceLineInput[];
}

export interface EInvoiceDispatch {
  readonly id: string;
  readonly invoiceId: string;
  readonly invoiceNumber: string | null;
  readonly format: string;
  readonly provider: string;
  readonly receiverId: string | null;
  readonly status: 'pending' | 'sent' | 'failed';
  readonly messageId: string | null;
  readonly documentSha256: string;
  readonly error: string | null;
  readonly requestedAt: string;
  readonly completedAt: string | null;
}

export interface SendEInvoiceResult {
  readonly dispatchId: string;
  readonly invoiceNumber: string;
  readonly format: string;
  readonly provider: string;
  readonly status: string;
  readonly messageId: string | null;
  /**
   * Whether the document actually left this machine.
   *
   * **False for the dry-run provider, which is what runs today.** `status` is
   * `sent` in that case too, so reading `status` alone tells a person their
   * invoice reached a customer when nothing opened a socket. This is the field
   * the UI shows.
   */
  readonly delivered: boolean;
  readonly documentSha256: string;
}

export type EInvoiceFormat = 'oioubl' | 'peppol_bis';

export interface InvoiceQuery {
  readonly status?: Invoice['status'] | undefined;
  readonly contactId?: string | undefined;
  readonly limit?: number | undefined;
}

export function useInvoices(query: InvoiceQuery = {}): UseQueryResult<Paged<Invoice>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    status: query.status,
    contactId: query.contactId,
    limit: query.limit ?? 200,
  });

  return useQuery({
    queryKey: keys.invoices(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<Invoice>>(`/v1/invoices${suffix}`);
    },
    enabled: api !== null,
  });
}

/** One invoice with its lines. `null` disables the query. */
export function useInvoice(id: string | null): UseQueryResult<Invoice> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.invoice(companyId ?? '', id ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Invoice>(`/v1/invoices/${id ?? ''}`);
    },
    enabled: api !== null && id !== null,
  });
}

export function useCreateInvoice(): UseMutationResult<Invoice, unknown, CreateInvoice> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateInvoice) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Invoice>('/v1/invoices', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', companyId ?? ''] });
    },
  });
}

/** Drafts only. An issued invoice describes a document the customer holds. */
export function useDeleteInvoice(): UseMutationResult<void, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<void>(`/v1/invoices/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', companyId ?? ''] });
    },
  });
}

/**
 * Issue a draft.
 *
 * Allocates the next number and posts the invoice to the ledger, in one
 * transaction. Irreversible: after this the invoice is frozen and a mistake is
 * corrected by credit note, never by editing or deleting — the number has to
 * stay in a run the law requires to be unbroken.
 */
export function useIssueInvoice(): UseMutationResult<Invoice, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Invoice>(`/v1/invoices/${id}/issue`, {
        method: 'POST',
        body: {},
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', companyId ?? ''] });
      // Issuing posts to the ledger, so the journal and the accounts moved.
      void queryClient.invalidateQueries({ queryKey: ['journal', companyId ?? ''] });
      void queryClient.invalidateQueries({ queryKey: ['accounts', companyId ?? ''] });
    },
  });
}

export function useMarkInvoicePaid(): UseMutationResult<Invoice, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Invoice>(`/v1/invoices/${id}/paid`, { method: 'POST' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', companyId ?? ''] });
    },
  });
}

/**
 * Send an invoice electronically.
 *
 * Goes out **once per format**: a second send would put a duplicate in the
 * customer's accounts payable, which is theirs to find and reverse. A failed
 * attempt leaves the format free to retry.
 */
export function useSendEInvoice(): UseMutationResult<
  SendEInvoiceResult,
  unknown,
  { id: string; format: EInvoiceFormat }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, format }: { id: string; format: EInvoiceFormat }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<SendEInvoiceResult>(`/v1/invoices/${id}/einvoice/send`, {
        method: 'POST',
        body: { format },
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoices', companyId ?? ''] });
    },
  });
}

export function useEInvoiceDispatches(
  query: { readonly invoiceId?: string | undefined } = {},
): UseQueryResult<Paged<EInvoiceDispatch>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ invoiceId: query.invoiceId, limit: 200 });

  return useQuery({
    queryKey: keys.dispatches(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<EInvoiceDispatch>>(
        `/v1/invoices/einvoice/dispatches${suffix}`,
      );
    },
    enabled: api !== null,
  });
}

/**
 * Build the UBL document without sending it.
 *
 * `requestText`, not `request`: this endpoint answers XML, and parsing it as
 * JSON fails on the first character. Building is read-only and repeatable, so
 * the document can be inspected before anything reaches a customer — but it is
 * a mutation here for the same reason a download URL is: it is fetched once,
 * on a click, and there is nothing to cache.
 */
export function useEInvoiceDocument(): UseMutationResult<
  string,
  unknown,
  { id: string; format: EInvoiceFormat }
> {
  const { api } = useAuth();

  return useMutation({
    mutationFn: ({ id, format }: { id: string; format: EInvoiceFormat }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.requestText(`/v1/invoices/${id}/einvoice?format=${format}`);
    },
  });
}

// -----------------------------------------------------------------------------
// VAT
// -----------------------------------------------------------------------------

export interface VatPeriod {
  readonly id: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: 'open' | 'closed';
  /**
   * Null while the period is open — not zero.
   *
   * Nothing is stored until closing: an open period's figures live in the
   * journal and are summed on request. A stored copy would be a second version
   * free to disagree with the books.
   */
  readonly outgoingVat: string | null;
  readonly incomingVat: string | null;
  readonly netVat: string | null;
  readonly closedAt: string | null;
  readonly closedBy: string | null;
}

export interface VatCodeTotal {
  readonly code: string;
  readonly label: string;
  readonly type: string;
  readonly vatGroup: string;
  /** Null where the official code has no fixed rate. Never render this as 0 %. */
  readonly rate: string | null;
  readonly rateNote: string | null;
  readonly baseAmount: string;
  readonly vatAmount: string;
  readonly lineCount: number;
}

export interface VatGroupTotal {
  readonly vatGroup: string;
  readonly type: string;
  readonly baseAmount: string;
  readonly vatAmount: string;
  readonly lineCount: number;
}

export interface VatReport {
  readonly periodId: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly status: string;
  /** Salgsmoms. */
  readonly outgoingVat: string;
  /** Købsmoms. */
  readonly incomingVat: string;
  /** Momstilsvar: salgsmoms − købsmoms. Positive is payable to SKAT. */
  readonly netVat: string;
  readonly byGroup: VatGroupTotal[];
  readonly byCode: VatCodeTotal[];
  /**
   * Codes used whose official rate is not a fixed number.
   *
   * Fourteen of the official codes are like this. For those the posted VAT
   * cannot be checked against a rate, so it rests on whoever posted it.
   */
  readonly codesWithoutFixedRate: string[];
  /**
   * Lines carrying no VAT code.
   *
   * Usually correct — a bank transfer or a wage payment has none. But a
   * purchase with no code is VAT the company is entitled to and has not
   * claimed, and only a person can tell which it is.
   */
  readonly uncodedLineCount: number;
  readonly entryCount: number;
  /** False while the period is open: the figures will still move. */
  readonly isFinal: boolean;
}

export interface CloseVatPeriodResult {
  readonly period: VatPeriod;
  readonly report: VatReport;
}

export function useVatPeriods(): UseQueryResult<Paged<VatPeriod>> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.vatPeriods(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<VatPeriod>>('/v1/vat/periods');
    },
    enabled: api !== null,
  });
}

/** The figures for one period. `null` disables the query. */
export function useVatReport(periodId: string | null): UseQueryResult<VatReport> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.vatReport(companyId ?? '', periodId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<VatReport>(`/v1/vat/periods/${periodId ?? ''}/report`);
    },
    enabled: api !== null && periodId !== null,
  });
}

export function useOpenVatPeriod(): UseMutationResult<
  VatPeriod,
  unknown,
  { periodStart: string; periodEnd: string }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { periodStart: string; periodEnd: string }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<VatPeriod>('/v1/vat/periods', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vat', companyId ?? ''] });
    },
  });
}

/**
 * File a period.
 *
 * Irreversible in three ways: the figures are frozen onto the period, the
 * period cannot be reopened, and nothing can ever be posted with a date inside
 * it again. The database enforces all three. Corrections belong in the
 * following period, which is how a Danish VAT correction works anyway.
 *
 * Owner-only — filing is the company declaring to SKAT, not a bookkeeping step.
 */
export function useCloseVatPeriod(): UseMutationResult<
  CloseVatPeriodResult,
  unknown,
  string
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (periodId: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<CloseVatPeriodResult>(`/v1/vat/periods/${periodId}/close`, {
        method: 'POST',
        body: {},
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['vat', companyId ?? ''] });
    },
  });
}

// -----------------------------------------------------------------------------
// Reports
// -----------------------------------------------------------------------------

/** A from/to window. Both ends optional: the API defaults to everything. */
export interface ReportPeriod {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
}

export interface ReportLine {
  readonly code: string;
  readonly name: string;
  readonly amount: string;
}

export interface TrialBalanceRow {
  readonly accountId: string;
  readonly code: string;
  readonly name: string;
  readonly type: string;
  readonly debit: string;
  readonly credit: string;
  readonly balance: string;
  readonly lineCount: number;
}

export interface TrialBalance {
  readonly from: string | null;
  readonly to: string | null;
  readonly dimension: string | null;
  readonly rows: TrialBalanceRow[];
  readonly totalDebit: string;
  readonly totalCredit: string;
  readonly difference: string;
  /**
   * Should always be true.
   *
   * The database refuses an unbalanced entry, so a false here is not a
   * bookkeeping mistake — it is the ledger disagreeing with itself, and worth
   * showing loudly rather than tucking into a footer.
   */
  readonly inBalance: boolean;
}

export interface ProfitAndLoss {
  readonly from: string | null;
  readonly to: string | null;
  readonly dimension: string | null;
  readonly revenue: ReportLine[];
  readonly expenses: ReportLine[];
  readonly totalRevenue: string;
  readonly totalExpenses: string;
  /** Negative is a loss. */
  readonly result: string;
}

export interface BalanceSheet {
  readonly from: string | null;
  readonly to: string | null;
  readonly dimension: string | null;
  readonly assets: ReportLine[];
  readonly liabilities: ReportLine[];
  readonly equity: ReportLine[];
  readonly totalAssets: string;
  readonly totalLiabilities: string;
  readonly totalEquity: string;
  /**
   * Carried separately rather than folded into equity.
   *
   * Nothing has closed the profit and loss accounts into equity — that is a
   * year-end posting this system does not make yet. Leaving the result out
   * would show a difference that is not a difference.
   */
  readonly resultForPeriod: string;
  readonly difference: string;
  readonly inBalance: boolean;
}

export function useTrialBalance(period: ReportPeriod = {}): UseQueryResult<TrialBalance> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ from: period.from, to: period.to });

  return useQuery({
    queryKey: keys.report(companyId ?? '', 'trial-balance', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<TrialBalance>(`/v1/reports/trial-balance${suffix}`);
    },
    enabled: api !== null,
  });
}

export function useProfitAndLoss(
  period: ReportPeriod = {},
): UseQueryResult<ProfitAndLoss> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ from: period.from, to: period.to });

  return useQuery({
    queryKey: keys.report(companyId ?? '', 'profit-and-loss', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<ProfitAndLoss>(`/v1/reports/profit-and-loss${suffix}`);
    },
    enabled: api !== null,
  });
}

export function useBalanceSheet(period: ReportPeriod = {}): UseQueryResult<BalanceSheet> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ from: period.from, to: period.to });

  return useQuery({
    queryKey: keys.report(companyId ?? '', 'balance-sheet', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<BalanceSheet>(`/v1/reports/balance-sheet${suffix}`);
    },
    enabled: api !== null,
  });
}

// -----------------------------------------------------------------------------
// SAF-T
// -----------------------------------------------------------------------------

/**
 * Generate the SAF-T file.
 *
 * `requestText`, not `request`: the endpoint answers XML and parsing it as JSON
 * would fail on the first character. A refusal still arrives as the usual JSON
 * envelope and is thrown as an ApiError, which is the point — the refusals are
 * the useful part of this feature and each of them names something to fix.
 *
 * A mutation because it is generated on a click, and because the file is
 * produced from the ledger at that moment rather than cached.
 */
export function useGenerateSaft(): UseMutationResult<
  string,
  unknown,
  { from: string; to: string }
> {
  const { api } = useAuth();

  return useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.requestText(`/v1/export/saft${queryString({ from, to })}`);
    },
  });
}

// -----------------------------------------------------------------------------
// Dashboard
// -----------------------------------------------------------------------------

/**
 * What is outstanding.
 *
 * The counts come from the database, not from the length of a list response.
 * That distinction is the whole reason the endpoint exists: `useAttachments`
 * returns at most `limit` rows and no total, so counting its items would show
 * the page size to anyone with a real backlog. See docs/reference/dashboard.md.
 */
export interface DashboardCounts {
  readonly unlinkedAttachments: number;
  /** Posteringer uden bilag — the opposite backlog, and the more serious one. */
  readonly undocumentedEntries: number;
  readonly unmatchedBankTransactions: number;
  /** Matched, but the entries do not add up to the amount. */
  readonly partiallyMatchedBankTransactions: number;
  /**
   * Kladdeposteringer, written but not yet posted.
   *
   * On the dashboard because that is the only place they appear at all: a
   * draft is excluded from every report by design, so a forgotten one is
   * invisible exactly where someone would otherwise notice it.
   */
  readonly draftEntries: number;
  readonly openVatPeriods: number;
  readonly oldestOpenVatPeriod: {
    readonly id: string;
    readonly periodStart: string;
    readonly periodEnd: string;
  } | null;
  /** No attachment and no bank transaction needs a person. */
  readonly clear: boolean;
}

export function useDashboard(): UseQueryResult<DashboardCounts> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.dashboard(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<DashboardCounts>('/v1/dashboard');
    },
    enabled: api !== null,
  });
}

// -----------------------------------------------------------------------------
// Company settings
// -----------------------------------------------------------------------------

export interface Company {
  readonly id: string;
  readonly name: string;
  readonly cvr: string | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly zip: string | null;
  readonly country: string;
  /** `MM-DD`. A fiscal year start has no year. */
  readonly fiscalYearStart: string;
  readonly vatPeriod: 'monthly' | 'quarterly' | 'biannual';
  readonly currency: string;
}

/**
 * The patch.
 *
 * `null` clears a field and `undefined` leaves it alone, matching the API. The
 * two are different instructions and the form has to be able to send either.
 *
 * `currency` and `country` are absent on purpose: the API does not accept them,
 * because every posted amount is already in the company currency and changing
 * it would reinterpret the ledger rather than convert it.
 */
export interface UpdateCompany {
  readonly name?: string | undefined;
  readonly cvr?: string | null | undefined;
  readonly address?: string | null | undefined;
  readonly city?: string | null | undefined;
  readonly zip?: string | null | undefined;
  readonly fiscalYearStart?: string | undefined;
  readonly vatPeriod?: Company['vatPeriod'] | undefined;
}

export function useCompany(): UseQueryResult<Company> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.company(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Company>('/v1/companies/current');
    },
    enabled: api !== null,
  });
}

/**
 * Save the settings.
 *
 * The company switcher does not read this query -- it reads the membership list
 * held in the auth context -- so a rename would leave the old name in the
 * header. The settings page calls the context's own `refresh` for that, rather
 * than this hook inventing a second way to reach the same state.
 */
export function useUpdateCompany(): UseMutationResult<Company, unknown, UpdateCompany> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: UpdateCompany) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Company>('/v1/companies/current', { method: 'PATCH', body });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.company(companyId ?? '') });
    },
  });
}

// -----------------------------------------------------------------------------
// Inbound document email
// -----------------------------------------------------------------------------

export interface InboundAddress {
  readonly localPart: string;
  /** Null when the deployment has no receiving domain: nothing can reach it. */
  readonly address: string | null;
  readonly isActive: boolean;
}

export interface InboundSender {
  readonly id: string;
  readonly email: string;
  readonly note: string | null;
  readonly createdAt: string;
}

export type InboundOutcome =
  | 'accepted'
  | 'sender_not_allowed'
  | 'duplicate'
  | 'unknown_address'
  | 'partially_accepted'
  | 'no_attachments';

export interface InboundMessage {
  readonly id: string;
  readonly provider: string;
  readonly providerMessageId: string;
  readonly fromEmail: string;
  readonly subject: string | null;
  readonly outcome: InboundOutcome;
  /** Why it was not accepted. The actionable part. */
  readonly detail: string | null;
  readonly attachmentCount: number;
  readonly storedCount: number;
  readonly receivedAt: string;
}

/** `null` when no address has been set for this company. */
export function useInboundAddress(): UseQueryResult<InboundAddress | null> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.inboundAddress(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<InboundAddress | null>('/v1/inbound-email/address');
    },
    enabled: api !== null,
  });
}

export function useSetInboundAddress(): UseMutationResult<
  InboundAddress,
  unknown,
  { localPart: string; isActive?: boolean }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { localPart: string; isActive?: boolean }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<InboundAddress>('/v1/inbound-email/address', {
        method: 'PUT',
        body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.inboundAddress(companyId ?? ''),
      });
    },
  });
}

export function useInboundSenders(): UseQueryResult<Paged<InboundSender>> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.inboundSenders(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<InboundSender>>('/v1/inbound-email/senders');
    },
    enabled: api !== null,
  });
}

export function useApproveInboundSender(): UseMutationResult<
  InboundSender,
  unknown,
  { email: string; note?: string }
> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: { email: string; note?: string }) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<InboundSender>('/v1/inbound-email/senders', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.inboundSenders(companyId ?? ''),
      });
      // A newly approved sender changes how past refusals read, so the log is
      // refetched too.
      void queryClient.invalidateQueries({ queryKey: ['inbound-email', companyId ?? ''] });
    },
  });
}

export function useRevokeInboundSender(): UseMutationResult<void, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<void>(`/v1/inbound-email/senders/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: keys.inboundSenders(companyId ?? ''),
      });
    },
  });
}

export interface InboundMessageQuery {
  readonly outcome?: InboundOutcome | undefined;
  readonly limit?: number | undefined;
}

export function useInboundMessages(
  query: InboundMessageQuery = {},
): UseQueryResult<Paged<InboundMessage>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ outcome: query.outcome, limit: query.limit ?? 100 });

  return useQuery({
    queryKey: keys.inboundMessages(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<InboundMessage>>(`/v1/inbound-email/messages${suffix}`);
    },
    enabled: api !== null,
  });
}


// -----------------------------------------------------------------------------
// The user's own account — Min konto.
//
// Two different credentials are in play here, and the split is not arbitrary:
//
//   /v1/users/me        needs a **company token**, like everything under /v1
//   /v1/auth/tokens     needs the **session cookie**, because a personal access
//                       token is what it hands out; a company token is refused
//   POST /v1/companies  needs the session too — you cannot hold a token for a
//                       company that does not exist yet
//
// So the token hooks go through `sessionClient()` rather than the context's
// `api`. Sending a company token to those endpoints answers 401, which reads
// like a session problem and is not.
// -----------------------------------------------------------------------------

export interface Profile {
  readonly id: string;
  readonly displayName: string | null;
  /** From `ba_users.two_factor_enabled` — see migration 0018. */
  readonly mfaEnabled: boolean;
  readonly locale: 'da' | 'en';
  readonly lastLoginAt: string | null;
}

export function useProfile(): UseQueryResult<Profile> {
  const { api } = useAuth();

  return useQuery({
    queryKey: keys.profile(),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Profile>('/v1/users/me');
    },
    enabled: api !== null,
  });
}

/** Either field alone, so changing the language does not clear the name. */
export interface ProfilePatch {
  readonly displayName?: string | null;
  readonly locale?: 'da' | 'en';
}

export function useUpdateProfile(): UseMutationResult<Profile, unknown, ProfilePatch> {
  const { api } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (patch: ProfilePatch) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Profile>('/v1/users/me', { method: 'PATCH', body: patch });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.profile(), updated);
    },
  });
}

export interface AccessToken {
  /** What this key may do. See ADR-009. */
  readonly scopes: TokenScope[];
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly expiresAt: string | null;
  /** Coarse — written at most every five minutes. "Still in use?", not "when". */
  readonly lastUsedAt: string | null;
  readonly revokedAt: string | null;
}

export function useAccessTokens(): UseQueryResult<Paged<AccessToken>> {
  const { hasSession } = useAuth();

  return useQuery({
    queryKey: keys.accessTokens(),
    queryFn: () => sessionClient().request<Paged<AccessToken>>('/v1/auth/tokens'),
    enabled: hasSession,
  });
}

export interface CreatedAccessToken {
  /** Present in this response and in no other, ever. It is stored as a hash. */
  readonly token: string;
  readonly record: AccessToken;
}

export function useCreateAccessToken(): UseMutationResult<
  CreatedAccessToken,
  unknown,
  { name: string; scopes?: readonly TokenScope[]; expiresAt?: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      name: string;
      scopes?: readonly TokenScope[];
      expiresAt?: string;
    }) =>
      sessionClient().request<CreatedAccessToken>('/v1/auth/tokens', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.accessTokens() });
    },
  });
}

export function useRevokeAccessToken(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await sessionClient().request<void>(`/v1/auth/tokens/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.accessTokens() });
    },
  });
}

export interface CreateCompany {
  readonly name: string;
  readonly cvr?: string;
}

/**
 * Create a company, becoming its owner.
 *
 * Session-authenticated, and refused with `company_creation_forbidden` unless
 * the caller already holds `owner` or `system_admin` somewhere (ADR-006 §6).
 * The switcher hides the control in that case, so this 403 should be
 * unreachable from the UI — it is the API's guarantee, not the UI's.
 *
 * An idempotency key, like every POST that creates a financial record: a
 * double-submitted form must not leave two companies, and a company that has
 * been written to can never be deleted.
 */
export function useCreateCompany(): UseMutationResult<Company, unknown, CreateCompany> {
  return useMutation({
    mutationFn: (input: CreateCompany) =>
      sessionClient().request<Company>('/v1/companies', {
        method: 'POST',
        body: input,
        idempotencyKey: uuid(),
      }),
  });
}

// -----------------------------------------------------------------------------
// The audit trail
// -----------------------------------------------------------------------------

export interface AuditEntry {
  readonly id: string;
  readonly action: string;
  readonly tableName: string | null;
  readonly recordId: string | null;
  readonly userId: string | null;
  readonly userName: string | null;
  readonly agentId: string | null;
  readonly accessTokenId: string | null;
  readonly accessTokenName: string | null;
  readonly payload: Record<string, unknown> | null;
  readonly createdAt: string;
}

export interface AuditPage {
  readonly items: AuditEntry[];
  readonly nextCursor: string | null;
}

export interface AuditQuery {
  readonly action?: string;
  readonly cursor?: string;
}

/**
 * A page of the audit trail.
 *
 * `retry: false`, unlike the default. The endpoint answers 403 for a role that
 * may not read it, and the shared retry rule already declines to retry a 4xx —
 * but this is stated here because the page *renders* that 403 as an explanation
 * rather than an error, and a retry would delay the explanation for no gain.
 */
export function useAuditEntries(query: AuditQuery = {}): UseQueryResult<AuditPage> {
  const { api, companyId } = useAuth();
  const suffix = queryString({ action: query.action, cursor: query.cursor });

  return useQuery({
    queryKey: keys.audit(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<AuditPage>(`/v1/audit${suffix}`);
    },
    enabled: api !== null,
    retry: false,
  });
}

/** The action names present in this company's log, for the filter. */
export function useAuditActions(): UseQueryResult<{ items: string[] }> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.auditActions(companyId ?? ''),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<{ items: string[] }>('/v1/audit/actions');
    },
    enabled: api !== null,
    retry: false,
  });
}

// -----------------------------------------------------------------------------
// Browser login for AI clients — the consent screen (ADR-008)
// -----------------------------------------------------------------------------

export interface AuthorizationCompany {
  readonly companyId: string;
  readonly role: string;
  readonly name: string | null;
}

export interface PendingAuthorization {
  /** What the client asked for. The screen offers these to be cleared. */
  readonly requestedScopes: TokenScope[];
  /** The client's own name for itself. Untrusted text — rendered, never built into markup. */
  readonly clientName: string;
  readonly redirectUri: string;
  readonly companies: readonly AuthorizationCompany[];
}

export function useAuthorizationRequest(
  id: string,
): UseQueryResult<PendingAuthorization> {
  const { hasSession } = useAuth();

  return useQuery({
    queryKey: keys.authorizationRequest(id),
    queryFn: () =>
      sessionClient().request<PendingAuthorization>(`/v1/auth/oauth/requests/${id}`),
    enabled: hasSession && id !== '',
    /*
      Never retried, and not cached beyond this screen. The request expires in
      ten minutes and can be approved once; a retry of a 404 would be a second
      way of saying the same thing, more slowly.
    */
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
}

export interface AuthorizationRedirect {
  readonly redirectTo: string;
}

export function useApproveAuthorization(): UseMutationResult<
  AuthorizationRedirect,
  unknown,
  { id: string; companyId: string; scopes?: readonly TokenScope[] }
> {
  return useMutation({
    mutationFn: (input: { id: string; companyId: string; scopes?: readonly TokenScope[] }) =>
      sessionClient().request<AuthorizationRedirect>(
        `/v1/auth/oauth/requests/${input.id}/approve`,
        {
          method: 'POST',
          body: {
            companyId: input.companyId,
            ...(input.scopes === undefined ? {} : { scopes: input.scopes }),
          },
        },
      ),
  });
}

export function useDenyAuthorization(): UseMutationResult<
  AuthorizationRedirect,
  unknown,
  string
> {
  return useMutation({
    mutationFn: (id: string) =>
      sessionClient().request<AuthorizationRedirect>(
        `/v1/auth/oauth/requests/${id}/deny`,
        { method: 'POST' },
      ),
  });
}

// -----------------------------------------------------------------------------
// Kørebog
// -----------------------------------------------------------------------------

/**
 * A trip in the kørebog.
 *
 * Every figure is a decimal string, including the distance: it is multiplied by
 * a rate to produce money, and a JSON number would round 42.10 somewhere along
 * the way. `deductionAmount` is the authority; `kmRate` is the effective rate,
 * exact within one tier and an average for the single trip that crosses the
 * 20.000 km threshold, which `tier1Km` and `tier2Km` explain.
 */
export interface Trip {
  readonly id: string;
  readonly userId: string;
  readonly date: string;
  readonly startAddress: string;
  readonly endAddress: string;
  readonly distanceKm: string;
  readonly purpose: string;
  readonly isBusiness: boolean;
  readonly rateTier1Dkk: string;
  readonly rateTier2Dkk: string;
  readonly thresholdKm: number;
  readonly yearToDateKm: string;
  readonly tier1Km: string;
  readonly tier2Km: string;
  readonly kmRate: string;
  readonly deductionAmount: string;
  readonly journalEntryId: string | null;
  readonly postedAt: string | null;
  readonly createdAt: string;
}

export interface KmRate {
  readonly id: string;
  readonly validFrom: string;
  readonly rateTier1Dkk: string;
  readonly rateTier2Dkk: string;
  readonly thresholdKm: number;
  readonly source: string;
}

export interface TripYearSummary {
  readonly year: number;
  readonly tripCount: number;
  readonly businessTripCount: number;
  readonly privateTripCount: number;
  readonly totalBusinessKm: string;
  readonly totalPrivateKm: string;
  readonly tier1Km: string;
  readonly tier2Km: string;
  readonly totalDeduction: string;
  readonly postedDeduction: string;
  readonly unpostedDeduction: string;
  readonly unpostedTripCount: number;
  readonly rates: KmRate[];
}

export interface CreateTrip {
  readonly date: string;
  readonly startAddress: string;
  readonly endAddress: string;
  readonly distanceKm: string;
  readonly purpose: string;
  readonly isBusiness?: boolean;
}

export interface TripQuery {
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly posted?: boolean | undefined;
}

export function useTrips(query: TripQuery = {}): UseQueryResult<Paged<Trip>> {
  const { api, companyId } = useAuth();
  const suffix = queryString({
    from: query.from,
    to: query.to,
    posted: query.posted,
  });

  return useQuery({
    queryKey: keys.trips(companyId ?? '', suffix),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<Trip>>(`/v1/trips${suffix}`);
    },
    enabled: api !== null,
  });
}

export function useTripYearSummary(year: number): UseQueryResult<TripYearSummary> {
  const { api, companyId } = useAuth();

  return useQuery({
    queryKey: keys.tripSummary(companyId ?? '', year),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<TripYearSummary>(`/v1/trips/summary/${String(year)}`);
    },
    enabled: api !== null,
  });
}

export function useCreateTrip(): UseMutationResult<Trip, unknown, CreateTrip> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateTrip) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Trip>('/v1/trips', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trips', companyId ?? ''] });
    },
  });
}

/**
 * Book a trip.
 *
 * The dashboard is invalidated as well: a posted trip creates a journal entry,
 * and an entry with no document behind it is one of the counts that page
 * reports. Leaving it stale would show a number the ledger disagrees with.
 */
export function usePostTrip(): UseMutationResult<Trip, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Trip>(`/v1/trips/${id}/post`, {
        method: 'POST',
        body: {},
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trips', companyId ?? ''] });
      void queryClient.invalidateQueries({ queryKey: keys.dashboard(companyId ?? '') });
      void queryClient.invalidateQueries({ queryKey: ['journal', companyId ?? ''] });
    },
  });
}

export function useDeleteTrip(): UseMutationResult<void, unknown, string> {
  const { api, companyId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<void>(`/v1/trips/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['trips', companyId ?? ''] });
    },
  });
}

export interface ExportedTrips {
  /** A blob: URL belonging to this tab. Revoke it once the download starts. */
  readonly url: string;
  readonly filename: string;
}

/**
 * Fetch the kørebog as a file.
 *
 * A mutation and uncached, like `useOpenAttachment`, and for the same reason:
 * this is "fetch this once, because somebody clicked". The bytes come through
 * the API under the token this client already holds and become a `blob:` URL
 * scoped to this tab — nothing shareable is produced.
 */
export function useExportTrips(): UseMutationResult<ExportedTrips, unknown, number> {
  const { api } = useAuth();

  return useMutation({
    mutationFn: async (year: number) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      const { bytes, contentType, filename } = await api.requestBytes(
        `/v1/trips/export?year=${String(year)}&format=csv`,
      );
      const blob = new Blob([bytes as BlobPart], { type: contentType });
      return {
        url: URL.createObjectURL(blob),
        filename: filename ?? `koerebog-${String(year)}.csv`,
      };
    },
  });
}

// -----------------------------------------------------------------------------
// Notifications
// -----------------------------------------------------------------------------

export interface PushConfig {
  readonly enabled: boolean;
  /** The VAPID public key, or null when the server has none configured. */
  readonly publicKey: string | null;
}

export interface PushDevice {
  readonly id: string;
  readonly userAgent: string | null;
  readonly createdAt: string;
  readonly lastSentAt: string | null;
  readonly expiredAt: string | null;
}

export function usePushConfig(): UseQueryResult<PushConfig> {
  const { api } = useAuth();

  return useQuery({
    queryKey: keys.pushConfig(),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<PushConfig>('/v1/notifications/config');
    },
    enabled: api !== null,
    // The server's VAPID key changes when a deployment rotates it, which is
    // rare enough that refetching on every mount is waste.
    staleTime: 60 * 60 * 1000,
  });
}

export function usePushDevices(): UseQueryResult<Paged<PushDevice>> {
  const { api } = useAuth();

  return useQuery({
    queryKey: keys.pushDevices(),
    queryFn: () => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<Paged<PushDevice>>('/v1/notifications/devices');
    },
    enabled: api !== null,
  });
}

export interface SubscribePush {
  readonly endpoint: string;
  readonly keys: { readonly p256dh: string; readonly auth: string };
}

export function useSubscribePush(): UseMutationResult<
  { id: string },
  unknown,
  SubscribePush
> {
  const { api } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: SubscribePush) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<{ id: string }>('/v1/notifications/subscriptions', {
        method: 'POST',
        body,
        idempotencyKey: uuid(),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.pushDevices() });
    },
  });
}

export function useUnsubscribePush(): UseMutationResult<void, unknown, string> {
  const { api } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (endpoint: string) => {
      if (api === null) {
        throw new Error('No company selected.');
      }
      return api.request<void>('/v1/notifications/subscriptions', {
        method: 'DELETE',
        body: { endpoint },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.pushDevices() });
    },
  });
}
